import { Actor, ActorInfo, ActorsInfo, Line } from './mini-dialog-types';
import { actorReducer } from './actor-reducer';
import { MiniDialogGetResponse, getMiniDialog, getSignedUrls } from './util/mini-dialog-api';
import { MiniDialogLine, MiniDialogLineProps } from './components/mini-dialog-line';
import { MiniDialogMedialink } from '../../../../api/medialink';
import { ToggleTextButton } from './components/toggle-text-button';
import { TOKEN_ACCESSOR } from '../../../../utils/tokenAccessor';
import { useAudioRecorder } from './hooks/useAudioRecorder';
import { ComponentProps, useCallback, useContext, useEffect, useMemo, useReducer, useState } from 'react';
import classNames from 'classnames';
import styles from './mini-dialog.module.css';
import useMediaPlayer from './hooks/useMediaPlayer';
import useThrowAsyncError from '../../../../hooks/useThrowAsyncError';
import MaterialDialogHeader from '../../../../pages/material/components/MaterialDialogHeader';
import { MedialinkDialogHeaderTags } from '../../../../pages/material/components/MedialinkDialogHeaderTags';
import ModuleContext from '../../../../pages/material/_contexts/ModuleContext';

import Dialog from '@pelckmans/elementis/components/dialog';
import Button from '@pelckmans/elementis/components/button';

import PlayIcon from '@pelckmans/elementis/icons/play';
import PauseIcon from '@pelckmans/elementis/icons/pause';

const Mode = {
  DIALOG: 'DIALOG',
  SINGLE_LINE: 'SINGLE_LINE',
} as const;

export type PlayMode = typeof Mode[keyof typeof Mode];

export interface MiniDialogModalTranslations {
  closeDialog: string;
  generalNode: string;
  playDialog: string;
  stopDialog: string;
  example: string;
  ownVoice: string;
  optionsTextAndSound: string;
  optionsKeyWords: string;
  playTitle: string;
  pauseTitle: string;
  recordTitle: string;
  stopRecordTitle: string;
  playRecordingTitle: string;
  pauseRecordingTitle: string;
  hideTextTitle: string;
  showTextTitle: string;
  hideKeywordsTitle: string;
  showKeywordsTitle: string;
  muteTitle: string;
  unmuteTitle: string;
  errors: {
    browserSupport: string;
    accessDenied: string;
    noMicrophone: string;
  };
  a11y: {
    label: string;
  };
}

const Notification: (notification: string) => JSX.Element = notification => {
  return (
    <div className="bc-notification__banner bc-notification__banner--warning">
      <i className="bc-icon bc-icon-info bc-icon--left" />
      <div dangerouslySetInnerHTML={{ __html: notification }} />
    </div>
  );
};

export interface MiniDialogModalProps {
  mediaLink: MiniDialogMedialink;
  onClose: () => void;
  urlPrefix?: string;
  studioGroup: string;
  superModuleHasExerciseEntrance?: boolean;
  translations: MiniDialogModalTranslations;
}

export function MiniDialogModal({
  mediaLink,
  onClose,
  urlPrefix,
  studioGroup,
  translations,
}: MiniDialogModalProps): JSX.Element | null {
  const { allModules } = useContext(ModuleContext);
  const medialinkModule = allModules.find(module => module.id === mediaLink.moduleId);

  const handleError = useThrowAsyncError();

  const [keywordsHidden, setKeywordsHidden] = useState(false);
  const [miniDialog, setMiniDialog] = useState<MiniDialogGetResponse>();
  const [urlData, setUrlData] = useState<{ [key: string]: string } | undefined>();
  const [currentPlayMode, setCurrentPlayMode] = useState<PlayMode>(Mode.DIALOG);
  const [actorsByDialogOrder, setActorsByDialogOrder] = useState<ActorInfo['actorId'][]>();
  const [mediaPlayerRef, { initialize, urlLoaded, play, pause, stop, mute, unmute, status: mediaPlayerStatus }] =
    useMediaPlayer(handleError);

  const [actors, dispatch] = useReducer(actorReducer, {} as ActorsInfo);

  useEffect(() => {
    async function getMiniDialogData(): Promise<void> {
      const token = await TOKEN_ACCESSOR.tokenGetter();

      const {
        id: medialinkId,
        moduleId,
        miniDialog: { id: miniDialogId },
      } = mediaLink;

      try {
        const response = await getMiniDialog(token, moduleId, medialinkId, miniDialogId, studioGroup);
        setMiniDialog(response);
      } catch (error) {
        handleError(error);
      }
    }

    getMiniDialogData();
  }, [mediaLink, handleError, studioGroup]);

  useEffect(() => {
    if (!miniDialog) return;

    const usedActors = miniDialog.actors.filter(actor => miniDialog.lines.find(line => line.actorId === actor.actorId));
    setActorsByDialogOrder(usedActors.map(actor => actor.actorId));

    dispatch({ type: 'INITIALIZE_ACTORS', payload: usedActors });
  }, [miniDialog]);

  useEffect(() => {
    async function getSignedUrlData(lines: Line[]): Promise<void> {
      const token = await TOKEN_ACCESSOR.tokenGetter();
      const fileIds = lines.map((line: Line) => line.audioFile).filter(x => x) as string[];

      try {
        const data: { [key: string]: string } = await getSignedUrls(token!, fileIds);
        const sortedByDialog: { [key: string]: string } = {};
        fileIds.forEach(fileId => (sortedByDialog[fileId] = data[fileId]));
        setUrlData(sortedByDialog);
      } catch (error) {
        handleError(error);
      }
    }

    if (!miniDialog) return;

    const { lines } = miniDialog;
    getSignedUrlData(lines);
  }, [miniDialog, handleError]);

  const initializeAsMP3 = useCallback((urls: string[]): void => initialize(urls, 'mp3'), [initialize]);

  useEffect(() => {
    if (!urlData) return;

    initializeAsMP3(Object.values(urlData));
  }, [initializeAsMP3, urlData]);

  const actorForAudioUrl = useMemo(() => {
    if (!miniDialog || !urlData) return;

    return miniDialog.lines.reduce((actorForAudioUrl: { [key: string]: Actor['actorId'] }, line) => {
      if (line.audioFile) {
        const fileUrl: string = urlData[line.audioFile];
        actorForAudioUrl[fileUrl] = line.actorId;
      }

      return actorForAudioUrl;
    }, {});
  }, [miniDialog, urlData]);

  useEffect(() => {
    if (!actorForAudioUrl || !urlLoaded || !actors || mediaPlayerStatus !== 'playing') return;

    const actorId: Actor['actorId'] = actorForAudioUrl[urlLoaded];
    const actor = actors[actorId];

    if (!actor) return;

    if (actor.mute) mute();
    else unmute();
  }, [urlLoaded, mute, unmute, actors, actorForAudioUrl, mediaPlayerStatus]);

  const playAll = useCallback(() => {
    if (!urlData) return;

    setCurrentPlayMode(Mode.DIALOG);
    initializeAsMP3(Object.values(urlData));

    play();
  }, [setCurrentPlayMode, initializeAsMP3, play, urlData]);

  const playSingle = useCallback(
    (audioUrl: string) => {
      if (currentPlayMode === Mode.DIALOG) {
        setCurrentPlayMode(Mode.SINGLE_LINE);
        initializeAsMP3([audioUrl]);
      } else if (audioUrl !== urlLoaded) {
        initializeAsMP3([audioUrl]);
      }

      play();
    },
    [currentPlayMode, initializeAsMP3, setCurrentPlayMode, urlLoaded, play],
  );

  const { start: startRecording, status: audioRecorderStatus, stop: stopRecording } = useAudioRecorder(handleError);

  const denyRecordingRights = useMemo((): boolean => audioRecorderStatus !== 'idle', [audioRecorderStatus]);

  const handleRecording: MiniDialogLineProps['onRecord'] = useCallback(async (): Promise<
    { stopRecording: () => Promise<string> } | undefined
  > => {
    if (urlData && Object.keys(urlData).length) {
      stop();
    }

    const started = await startRecording();
    if (started) {
      return {
        stopRecording: async (): Promise<string> => {
          const recording = await stopRecording();

          return recording;
        },
      };
    }

    return undefined;
  }, [urlData, startRecording, stop, stopRecording]);

  const dialogPlaying = currentPlayMode === Mode.DIALOG && mediaPlayerStatus === 'playing';

  const denyPlayRights = useMemo(
    (): boolean => audioRecorderStatus === 'recording' || dialogPlaying,
    [audioRecorderStatus, dialogPlaying],
  );

  if (!medialinkModule) return null;

  const size: ComponentProps<typeof Dialog>['size'] = 'full';

  return (
    <Dialog
      id="mini-dialog-modal"
      aria-label={translations.a11y.label}
      modal
      size={size}
      onClose={!mediaLink.contentType.faqTutorial ? onClose : undefined}
    >
      <MaterialDialogHeader
        onClose={onClose}
        title={mediaLink.name}
        titleTooltip={mediaLink.name}
        tags={<MedialinkDialogHeaderTags material={mediaLink} module={medialinkModule} />}
        showCloseLabelAsText={size === 'full'}
      />
      <Dialog.Body>
        {audioRecorderStatus === 'no-media-devices' && Notification(translations.errors.browserSupport)}
        {audioRecorderStatus === 'access-denied' && Notification(translations.errors.accessDenied)}
        {audioRecorderStatus === 'no-microphone' && Notification(translations.errors.noMicrophone)}

        <div className={styles.hidden}>
          <div ref={mediaPlayerRef} id="media-player" />
        </div>

        {miniDialog && urlData && Object.values(actors).length > 0 && (
          <div className={styles.container} data-testid="container">
            <main className={styles.main}>
              <table className={styles.table}>
                <thead>
                  <tr className={styles.borderBottom}>
                    <th colSpan={3}>
                      {dialogPlaying ? (
                        <Button
                          size="md"
                          prominence="secondary"
                          className={styles.headerButton}
                          onClick={stop}
                          startIcon={<PauseIcon />}
                        >
                          <span>{translations.stopDialog}</span>
                        </Button>
                      ) : (
                        <Button
                          size="md"
                          prominence="secondary"
                          disabled={audioRecorderStatus === 'recording' || Object.keys(urlData).length === 0}
                          className={classNames(styles.headerButton, {
                            [styles.disabled]: audioRecorderStatus === 'recording',
                          })}
                          onClick={playAll}
                          startIcon={<PlayIcon />}
                        >
                          <span>{translations.playDialog}</span>
                        </Button>
                      )}
                    </th>
                    <th className={styles.headerTitle}>{translations.example}</th>
                    <th className={styles.headerTitle}>{translations.ownVoice}</th>
                  </tr>
                </thead>
                <tbody>
                  {miniDialog.lines.map(line => {
                    const { audioFile } = line;
                    const audioUrl = audioFile && urlData[audioFile];

                    return (
                      <MiniDialogLine
                        key={line._id}
                        actor={actors[line.actorId]!}
                        text={line.text}
                        keywordsHidden={keywordsHidden}
                        audioUrl={audioUrl}
                        urlLoaded={urlLoaded}
                        playMode={currentPlayMode}
                        mediaPlayerStatus={mediaPlayerStatus}
                        onPlay={denyPlayRights ? undefined : playSingle}
                        onPause={pause}
                        onRecord={denyRecordingRights ? undefined : handleRecording}
                        urlPrefix={urlPrefix}
                        translations={translations}
                      />
                    );
                  })}
                </tbody>
              </table>
            </main>
            <aside className={styles.aside}>
              {miniDialog.image && (
                <div
                  role="img"
                  className={styles.image}
                  style={{ backgroundImage: `url('${urlPrefix || ''}/api/files/${miniDialog.image}')` }}
                />
              )}
              <div className={styles.optionsWrapper}>
                <h2 className={styles.optionsHeader}>{translations.optionsTextAndSound}</h2>
                <ol className={classNames(styles.list, styles.options)}>
                  {actorsByDialogOrder!.map((actorId: ActorInfo['actorId']) => {
                    const actor = actors[actorId];
                    return (
                      actor && (
                        <li key={actor.name} className={classNames(styles.listItem, styles.option)}>
                          <span className={styles.optionName}>{actor.name}</span>
                          <ToggleTextButton
                            hideText={actor.hideText}
                            hideTextTitle={translations.hideTextTitle}
                            showTextTitle={translations.showTextTitle}
                            onClick={(): void => dispatch({ type: 'TOGGLE_TEXT_ACTOR', payload: actor.actorId })}
                          />
                          <button
                            type="button"
                            title={actor.mute ? translations.unmuteTitle : translations.muteTitle}
                            className={styles.optionButton}
                            onClick={(): void => dispatch({ type: 'TOGGLE_MUTE_ACTOR', payload: actor.actorId })}
                          >
                            <i className={`bc-icon bc-icon-sound${actor.mute ? '-muted' : ''}`} role="img" />
                          </button>
                        </li>
                      )
                    );
                  })}
                  <li className={classNames(styles.listItem, styles.optionKeywords)}>
                    <span className={styles.optionName}>{translations.optionsKeyWords}</span>
                    <ToggleTextButton
                      hideText={keywordsHidden}
                      hideTextTitle={translations.hideKeywordsTitle}
                      showTextTitle={translations.showKeywordsTitle}
                      onClick={(): void => setKeywordsHidden(hide => !hide)}
                    />
                  </li>
                </ol>
              </div>
            </aside>
          </div>
        )}
      </Dialog.Body>
    </Dialog>
  );
}

export default MiniDialogModal;
